import { useState, useEffect } from 'react';
import * as _ from 'lodash';
import { findModeByMIME, findModeByExtension, findModeByFileName } from 'codemirror';
import * as API from '../../../../../redux/api';
import { nuc } from '../../../../../utils';
import { useAsyncStatus } from '../../../../../hooks';
import type { FailedFetchResponse } from '../../../../../helpers/errors';

type CodeMirrorModeInfo = ReturnType<typeof findModeByFileName>;

//  Helper for building the URL
const getURL = (revisionLocator: string, filePath: string): `/api/revisions/${string}/blob/${string}` =>
  `/api/revisions/${nuc(revisionLocator)}/blob/${nuc(filePath)}`;

//  Determines the "mode" for CodeMirror to display a given file.
//  It can infer this from the `Content-type` header, or from the file extension.
const getCodeMirrorModeFromContentType = (filePath: string, contentType: string | null): CodeMirrorModeInfo => {
  const fileExtension = _.last(filePath.split('.')) || 'txt';
  return findModeByMIME(contentType || '') ||
    findModeByExtension(fileExtension) ||
    findModeByFileName(filePath);
};

/**
 * Hook which will fetch the contents of a revision file match.
 *
 * This will return the CodeMirror file "mode" (which informs it
 * about the type of file it is viewing) as well as the raw
 * contents of the file as a UTF-8 string.
 *
 * The file contents are re-fetched if the file path or revision locator change.
 */
function useRevisionFileBlob(revisionLocator: string, filePath: string) {
  //  TODO: Use the useAsyncValue hook when this lands: https://github.com/fossas/FOSSA/pull/10963
  const { status, onPending, onSuccess, onError } = useAsyncStatus();
  const [mode, setMode] = useState<CodeMirrorModeInfo>();
  const [fileContents, setFileContents] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    async function getFileBlob() {
      try {
        onPending();
        setIsNotFound(false);

        const [contentType, file] = await API.blobText(getURL(revisionLocator, filePath));

        setMode(getCodeMirrorModeFromContentType(filePath, contentType));
        setFileContents(file);

        onSuccess();
      } catch (error) {
        if (error?.message?.includes('Not Found')) {
          setIsNotFound(true);
        }
        onError(error as FailedFetchResponse);
      }
    }

    if (revisionLocator && filePath) {
      getFileBlob();
    }
  }, [revisionLocator, filePath, onPending, onSuccess, onError]);

  return {
    mode,
    status,
    isNotFound,
    fileContents,
  };
}

export default useRevisionFileBlob;
